'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, BellRing, CalendarDays, Check, Clock3, LoaderCircle, Minus, Plus, Timer, Users, X } from 'lucide-react'
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BrandMark } from '@/components/brand/brand-mark'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { createClientId } from '@/lib/client-id'
import type { AvailabilityOption, BookingIntent, PublicMenu, Venue } from './domain'
import { MenuSelectionEditor } from './menu-selection-editor'
import {
  type PublicMenuSelectionDraft,
  validatePublicMenuSelections,
} from './menu-selection-state'
import { StripePaymentStep, type ReservationPaymentSession } from './stripe-payment-step'

type Hold = {
  holdPublicId: string
  holdToken: string
  expiresAt: string
  businessDate: string
  localStart: string
  startsAt: string
  endsAt: string
  partySize: number
  policySnapshot: Record<string, unknown>
  questions: Array<{ id: string; label: string; required: boolean }>
  menu: PublicMenu | null
  menuSelections: Array<{
    guestNumber: number
    itemId: string
    itemName: string
    modifiers: Array<{
      groupId: string
      groupName: string
      options: Array<{ id: string; name: string }>
    }>
  }>
  paymentRequirement: { bookingMethod: 'free' | 'deposit' | 'prepaid' | 'card_hold'; currency: string; unitAmount: number; subtotalAmount: number; feeAmount: number; feeSnapshot: Array<{ id: string; name: string; priceCents: number; sku: string }>; totalAmount: number; discountAmount?: number; discountSnapshot?: { name: string; code: string } }
}

type PendingConfirmation = {
  holdPublicId: string
  holdToken: string
  guest: { name: string; email: string; phone?: string }
  answers: Record<string, string>
  consents: Record<string, boolean>
}

type Confirmation = {
  reservationPublicId: string
  confirmationCode: string
  managementToken: string
  startsAt: string
  partySize: number
}

type BookingProfile = {
  version: number
  firstName: string
  lastName: string
  email: string
  phone: string
}

type SavedPaymentMethod = {
  publicId: string
  brand: string
  last4: string
  expMonth?: number
  expYear?: number
  isDefault: boolean
}

const tomorrow = () => {
  const value = new Date()
  value.setDate(value.getDate() + 1)
  return toLocalDate(value)
}

export function BookingSheet({ venue, initialSearch, initialOption, onClose }: { venue: Venue; initialSearch?: BookingIntent; initialOption?: AvailabilityOption; onClose: () => void }) {
  const partyMinimum = venue.partyMin ?? 1
  const partyMaximum = venue.partyMax ?? 8
  const [step, setStep] = useState<'time' | 'holding' | 'menu' | 'details' | 'payment' | 'notify' | 'notify-confirmed' | 'confirmed'>(() => {
    if (initialSearch?.source === 'notify') return 'notify'
    if (initialOption && venue.menu) return 'menu'
    return initialOption ? 'holding' : 'time'
  })
  const [date, setDate] = useState<string>(initialSearch?.date ?? tomorrow)
  const [party, setParty] = useState(Math.min(partyMaximum, Math.max(partyMinimum, initialSearch?.partySize ?? 2)))
  const [options, setOptions] = useState<AvailabilityOption[]>([])
  const [selectedOption, setSelectedOption] = useState<AvailabilityOption | null>(() => initialOption && venue.menu ? initialOption : null)
  const [menu, setMenu] = useState(venue.menu)
  const [menuSelections, setMenuSelections] = useState<PublicMenuSelectionDraft[]>([])
  const [hold, setHold] = useState<Hold | null>(null)
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const [paymentSession, setPaymentSession] = useState<ReservationPaymentSession | null>(null)
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submittingStart, setSubmittingStart] = useState('')
  const [error, setError] = useState('')
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [notifyTime, setNotifyTime] = useState(initialSearch?.preferredTime ?? '')
  const [bookingProfile, setBookingProfile] = useState<BookingProfile | null>(null)
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<SavedPaymentMethod[]>([])
  const [discountCode, setDiscountCode] = useState('')
  const [checkoutEmail, setCheckoutEmail] = useState('')
  const [applyingDiscount, setApplyingDiscount] = useState(false)
  const confirmationKey = useRef<string | null>(null)
  const initialHoldAttempted = useRef(false)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    sheetRef.current?.scrollTo({ top: 0 })
  }, [step])

  const search = useCallback(async () => {
    if (!venue.tables) {
      setOptions([])
      setError('Online booking is not connected for this venue yet.')
      return
    }
    setLoading(true)
    setError('')
    try {
      const response = await fetch('/api/tables/availability/search', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...venue.tables,
          partySize: party,
          dateFrom: date,
          dateTo: date,
        }),
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.message ?? 'Availability could not be loaded.')
      const candidates = (payload.dates?.[0]?.options ?? []) as AvailabilityOption[]
      setOptions(candidates)
    } catch (cause) {
      setOptions([])
      setError(cause instanceof Error ? cause.message : 'Availability could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [date, party, venue.tables])

  useEffect(() => {
    if (step !== 'time') return
    const timeout = window.setTimeout(() => void search(), 0)
    return () => window.clearTimeout(timeout)
  }, [search, step])

  useEffect(() => {
    const controller = new AbortController()
    const loadProfile = async () => {
      try {
        const [profileResponse, methodsResponse] = await Promise.all([
          fetch('/api/tables/account/profile', { signal: controller.signal }),
          fetch('/api/tables/account/payment-methods', { signal: controller.signal }),
        ])
        if (profileResponse.ok) {
          const profile = (await profileResponse.json()) as BookingProfile
          setBookingProfile(profile)
          setCheckoutEmail((current) => current || profile.email)
        }
        if (methodsResponse.ok) setSavedPaymentMethods(((await methodsResponse.json()) as { data: SavedPaymentMethod[] }).data)
      } catch {
        // Guest checkout remains available when there is no signed-in account.
      }
    }
    void loadProfile()
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!hold || !['details', 'payment'].includes(step)) return
    const update = () => {
      const remaining = Math.max(0, Math.ceil((new Date(hold.expiresAt).getTime() - Date.now()) / 1000))
      setRemainingSeconds(remaining)
      if (remaining === 0) {
        setError('Your hold expired. Choose another time to continue.')
        setHold(null)
        setStep('time')
        void search()
      }
    }
    update()
    const interval = window.setInterval(update, 500)
    return () => window.clearInterval(interval)
  }, [hold, search, step])

  const effectiveMenuSelections = useMemo(() => {
    const orderableMenuItemIds = new Set(
      menu?.sections.flatMap((section) =>
        section.items
          .filter((item) => item.orderable !== false)
          .map((item) => item.id),
      ) ?? [],
    )
    return menuSelections.filter(
      (selection) =>
        selection.guestNumber <= party &&
        orderableMenuItemIds.has(selection.itemId),
    )
  }, [menu, menuSelections, party])
  const menuSelectionsValid = useMemo(
    () => validatePublicMenuSelections(menu, effectiveMenuSelections, party),
    [effectiveMenuSelections, menu, party],
  )

  const createHold = useCallback(async (option: AvailabilityOption) => {
    if (!option.optionToken) return
    setSubmitting(true)
    setSubmittingStart(option.startsAt)
    setError('')
    try {
      const response = await fetch('/api/tables/holds', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': createClientId() },
        body: JSON.stringify({
          optionToken: option.optionToken,
          menuSelections: effectiveMenuSelections,
        }),
      })
      const payload = await response.json()
      if (!response.ok) {
        if ('menu' in payload) setMenu((payload.menu as PublicMenu | null) ?? undefined)
        throw new Error(payload.message ?? payload.detail ?? 'That table is no longer available.')
      }
      const nextHold = payload as Hold
      setHold(nextHold)
      setDiscountCode(nextHold.paymentRequirement.discountSnapshot?.code ?? '')
      setPaymentSession(null)
      confirmationKey.current = createClientId()
      setSelectedOption(null)
      setStep('details')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That table is no longer available.')
      if (step === 'time') void search()
      else setStep('time')
    } finally {
      setSubmitting(false)
      setSubmittingStart('')
    }
  }, [effectiveMenuSelections, search, step])

  useEffect(() => {
    if (!initialOption || venue.menu || initialHoldAttempted.current) return
    initialHoldAttempted.current = true
    void createHold(initialOption)
  }, [initialOption, venue.menu, createHold])

  const chooseTime = (option: AvailabilityOption) => {
    if (!menu) {
      void createHold(option)
      return
    }
    setError('')
    setSelectedOption(option)
    setStep('menu')
  }

  const selectAvailabilityOption = (option: AvailabilityOption) => {
    if (option.bookable && option.optionToken) {
      chooseTime(option)
      return
    }
    setNotifyTime(option.localStart)
    setError('')
    setStep('notify')
  }

  const releaseHold = async () => {
    if (hold) {
      await fetch(`/api/tables/holds/${hold.holdPublicId}`, {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ holdToken: hold.holdToken }),
      })
    }
    setHold(null)
    setPaymentSession(null)
    setPendingConfirmation(null)
    setStep('time')
    void search()
  }

  const closeSheet = async () => {
    if (hold && ['details', 'payment'].includes(step)) {
      await fetch(`/api/tables/holds/${hold.holdPublicId}`, {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ holdToken: hold.holdToken }),
      })
    }
    onClose()
  }

  const applyDiscount = async (guestEmail: string) => {
    if (!hold) throw new Error('Your table hold is no longer active.')
    if (!discountCode.trim()) return hold
    if (!guestEmail.trim()) throw new Error('Enter your reservation email before applying a discount code.')

    const response = await fetch(`/api/tables/holds/${hold.holdPublicId}/discount`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        holdToken: hold.holdToken,
        discountCode: discountCode.trim(),
        guestEmail: guestEmail.trim(),
      }),
    })
    const payload = await response.json()
    if (!response.ok) throw new Error(payload.message ?? payload.detail ?? 'That discount code could not be applied.')
    const nextHold = payload as Hold
    setHold(nextHold)
    setDiscountCode(nextHold.paymentRequirement.discountSnapshot?.code ?? discountCode.trim())
    setPaymentSession(null)
    return nextHold
  }

  const handleApplyDiscount = async () => {
    setApplyingDiscount(true)
    setError('')
    try {
      await applyDiscount(checkoutEmail)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That discount code could not be applied.')
    } finally {
      setApplyingDiscount(false)
    }
  }

  const createPaymentSession = async (activeHold: Hold) => {
    const response = await fetch(`/api/tables/holds/${activeHold.holdPublicId}/payment-sessions`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'idempotency-key': createClientId() },
      body: JSON.stringify({ holdToken: activeHold.holdToken }),
    })
    const payload = await response.json()
    if (!response.ok) throw new Error(payload.message ?? 'Secure payment could not be started.')
    return payload as ReservationPaymentSession
  }

  const submitDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!hold || !confirmationKey.current) return
    const form = new FormData(event.currentTarget)
    const answers = Object.fromEntries(hold.questions.map((question) => [question.id, String(form.get(question.id) ?? '')]))
    const body = {
      holdPublicId: hold.holdPublicId,
      holdToken: hold.holdToken,
      guest: {
        name: `${String(form.get('firstName') ?? '').trim()} ${String(form.get('lastName') ?? '').trim()}`.trim(),
        email: String(form.get('email') ?? ''),
        phone: String(form.get('phone') ?? '') || undefined,
      },
      answers,
      consents: {
        termsAccepted: form.get('termsAccepted') === 'on',
        emailTransactional: true,
        emailMarketing: form.get('emailMarketing') === 'on',
        smsTransactional: form.get('smsTransactional') === 'on',
      },
    }
    setSubmitting(true)
    setError('')
    try {
      let checkoutHold = hold
      if (discountCode.trim() && !hold.paymentRequirement.discountAmount) {
        checkoutHold = await applyDiscount(body.guest.email)
      }
      if (checkoutHold.paymentRequirement.bookingMethod !== 'free') {
        const nextPaymentSession = paymentSession ?? await createPaymentSession(checkoutHold)
        setPaymentSession(nextPaymentSession)
        setPendingConfirmation(body)
        setStep('payment')
        return
      }
      await confirmReservation(body)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The reservation could not be confirmed.')
    } finally {
      setSubmitting(false)
    }
  }

  const confirmReservation = async (body: PendingConfirmation) => {
    if (!confirmationKey.current) throw new Error('The confirmation session expired.')
    setSubmitting(true)
    setError('')
    try {
      const response = await fetch('/api/tables/reservations', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': confirmationKey.current },
        body: JSON.stringify({ ...body, payment: paymentSession ? { sessionPublicId: paymentSession.publicId, sessionToken: paymentSession.sessionToken } : undefined }),
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.message ?? 'The reservation could not be confirmed.')
      setConfirmation(payload as Confirmation)
      window.localStorage.setItem(`tables:manage:${payload.reservationPublicId}`, payload.managementToken)
      setStep('confirmed')
    } finally {
      setSubmitting(false)
    }
  }

  const requestNotify = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!venue.tables) return
    const form = new FormData(event.currentTarget)
    setSubmitting(true)
    setError('')
    try {
      const response = await fetch('/api/tables/notify-requests', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...venue.tables,
          guestName: String(form.get('name') ?? ''),
          guestEmail: String(form.get('email') ?? ''),
          guestPhone: String(form.get('phone') ?? '') || undefined,
          partySize: party,
          requestedDates: [date],
          timeFrom: String(form.get('timeFrom') ?? '17:00'),
          timeUntil: String(form.get('timeUntil') ?? '21:00'),
          notes: String(form.get('notes') ?? '') || undefined,
        }),
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.message ?? 'Your notification request could not be saved.')
      window.localStorage.setItem(`tables:notify:${payload.publicId}`, payload.managementToken)
      setStep('notify-confirmed')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Your notification request could not be saved.')
    } finally {
      setSubmitting(false)
    }
  }

  const holdTime = `${Math.floor(remainingSeconds / 60).toString().padStart(2, '0')}:${(remainingSeconds % 60)
    .toString()
    .padStart(2, '0')}`
  return (
    <Sheet open onOpenChange={(open) => !open && void closeSheet()}>
      <SheetContent ref={sheetRef} className="booking-sheet" showCloseButton={false}>
        <SheetTitle className="sr-only">Book {venue.name}</SheetTitle>
        <div className="sheet-topbar">
          {step === 'details' ? <button onClick={() => void releaseHold()} aria-label="Release hold and go back"><ArrowLeft /></button> : step === 'menu' ? <button onClick={() => { setSelectedOption(null); setStep('time') }} aria-label="Back to availability"><ArrowLeft /></button> : step === 'notify' ? <button onClick={() => setStep('time')} aria-label="Back to availability"><ArrowLeft /></button> : <span />}
          <BrandMark />
          <button onClick={() => void closeSheet()} aria-label="Close"><X /></button>
        </div>

        {step === 'time' && (
          <div className="booking-step">
            <div className="booking-venue-image"><Image src={venue.image} alt={`${venue.name} dining room`} fill sizes="500px" /><div><p>{venue.neighborhood}</p><h2>{venue.name}</h2></div></div>
            <div className="booking-body">
              <p className="step-label">Reservations</p>
              <div className="booking-meta">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button className="booking-date" type="button"><CalendarDays size={17} /><span><small>Date</small>{formatDate(date)}</span></button>
                  </PopoverTrigger>
                  <PopoverContent className="booking-calendar" align="start">
                    <Calendar mode="single" selected={dateValue(date)} disabled={{ before: dateValue(todayDate()) }} onSelect={(nextDate) => { if (!nextDate) return; setDate(toLocalDate(nextDate)); setCalendarOpen(false) }} />
                  </PopoverContent>
                </Popover>
                <div className="party-stepper"><Users size={17} /><span><small>Party size</small>{party} {party === 1 ? 'guest' : 'guests'}</span><button disabled={party <= partyMinimum} onClick={() => setParty(Math.max(partyMinimum, party - 1))} aria-label="Remove guest"><Minus size={14} /></button><button disabled={party >= partyMaximum} onClick={() => setParty(Math.min(partyMaximum, party + 1))} aria-label="Add guest"><Plus size={14} /></button></div>
              </div>
              <div className="booking-experience-heading"><div><h3>{venue.offeringName ?? 'Dining reservation'}</h3></div><span>Parties of {partyMinimum}–{partyMaximum}</span></div>
              <p className="availability-note" aria-live="polite">{loading ? 'Checking availability…' : `${options.filter((option) => option.bookable).length} times available`}</p>
              <div className="booking-times" aria-busy={loading}>
                {options.map((option) => {
                  const bookable = option.bookable && Boolean(option.optionToken)
                  return <button key={option.startsAt} disabled={submitting} className={`${initialSearch?.preferredTime === option.localStart ? 'preferred ' : ''}${bookable ? '' : 'is-notify'}`.trim()} onClick={() => selectAvailabilityOption(option)}><span>{formatTime(option.startsAt)}</span><strong>{submittingStart === option.startsAt ? 'Holding…' : bookable ? 'Book' : 'Notify'}</strong></button>
                })}
              </div>
              {!loading && options.length === 0 && <p className="booking-empty">No tables are available for this date and party size.</p>}
              {error && <p className="booking-error" role="alert">{error}</p>}
              {!loading && options.length === 0 && <button className="notify-availability" onClick={() => { setNotifyTime(''); setError(''); setStep('notify') }}><BellRing /><span><strong>Get notified for this date</strong><small>Choose a time range and we’ll email you if a table opens.</small></span></button>}
            </div>
          </div>
        )}

        {step === 'holding' && initialOption && (
          <div className="booking-body booking-holding" aria-live="polite">
            <LoaderCircle aria-hidden="true" />
            <p className="step-label">Securing your table</p>
            <h2>{formatTime(initialOption.startsAt)}</h2>
            <div className="booking-summary">
              <strong>{venue.name}</strong>
              <span><CalendarDays size={15} /> {formatDate(date)}</span>
              <span><Users size={15} /> {party} guests</span>
            </div>
            <p>Holding your table for checkout.</p>
          </div>
        )}

        {step === 'menu' && menu && selectedOption && (
          <div className="booking-body booking-details booking-menu-step">
            <p className="step-label">Menu selections</p>
            <h2>Choose ahead for your table.</h2>
            <div className="booking-summary">
              <strong>{venue.name}</strong>
              <span><CalendarDays size={15} /> {formatDate(date)} at {formatTime(selectedOption.startsAt)}</span>
              <span><Users size={15} /> {party} guests</span>
            </div>
            <MenuSelectionEditor
              menu={menu}
              guestCount={party}
              value={effectiveMenuSelections}
              onChange={setMenuSelections}
            />
            {error && <p className="booking-error" role="alert">{error}</p>}
            <Button
              size="lg"
              className="button button--burgundy button--full"
              disabled={submitting || !menuSelectionsValid}
              onClick={() => void createHold(selectedOption)}
            >
              {submitting ? 'Holding your table…' : 'Continue with these choices'}
            </Button>
          </div>
        )}

        {step === 'details' && hold && (
          <form className="booking-body booking-details" onSubmit={submitDetails}>
            <div className="hold-banner"><Timer size={17} /><span><strong>We’re holding this table</strong><small>Complete your reservation in {holdTime}</small></span><i style={{ width: `${Math.min(100, (remainingSeconds / 300) * 100)}%` }} /></div>
            <p className="step-label">Your details</p>
            <h2>Complete your reservation.</h2>
            <div className="booking-summary"><strong>{venue.name}</strong><span><CalendarDays size={15} /> {formatDate(hold.businessDate)} at {formatTime(hold.startsAt)}</span><span><Users size={15} /> {hold.partySize} guests</span></div>
            {hold.menuSelections.length ? (
              <div className="booking-menu-summary">
                <strong>Pre-visit selections</strong>
                {hold.menuSelections.map((selection) => (
                  <p key={`${selection.guestNumber}:${selection.itemId}`}>
                    <span>Guest {selection.guestNumber} · {selection.itemName}</span>
                    <small>{selection.modifiers.flatMap((group) => group.options.map((option) => option.name)).join(', ') || 'No modifiers'}</small>
                  </p>
                ))}
              </div>
            ) : null}
            {hold.paymentRequirement.bookingMethod !== 'free' && <div className="booking-price-summary">
              {hold.paymentRequirement.unitAmount > 0 ? <span><span>{hold.paymentRequirement.bookingMethod === 'card_hold' ? 'Potential cancellation/no-show charge' : hold.paymentRequirement.bookingMethod === 'deposit' ? 'Deposit' : venue.offeringName ?? 'Experience'}</span><strong>{formatMoney(hold.paymentRequirement.unitAmount * hold.partySize, hold.paymentRequirement.currency)}</strong></span> : null}
              {hold.paymentRequirement.feeSnapshot?.map((fee) => <span key={fee.id}><span>{fee.name}</span><strong>{formatMoney(fee.priceCents, hold.paymentRequirement.currency)}</strong></span>)}
              {hold.paymentRequirement.discountAmount ? <span className="booking-price-discount"><span>{hold.paymentRequirement.discountSnapshot?.name ?? 'Discount'}</span><strong>−{formatMoney(hold.paymentRequirement.discountAmount, hold.paymentRequirement.currency)}</strong></span> : null}
              <span className="booking-price-total"><span>{hold.paymentRequirement.bookingMethod === 'card_hold' ? 'Potential total' : 'Total due'}</span><strong>{formatMoney(hold.paymentRequirement.totalAmount, hold.paymentRequirement.currency)}</strong></span>
            </div>}
            <div className="form-grid" key={bookingProfile?.version ?? 0}>
              <label>First name<Input name="firstName" autoComplete="given-name" defaultValue={bookingProfile?.firstName} required /></label>
              <label>Last name<Input name="lastName" autoComplete="family-name" defaultValue={bookingProfile?.lastName} required /></label>
              <label className="full">Email<Input name="email" type="email" autoComplete="email" value={checkoutEmail} readOnly={Boolean(hold.paymentRequirement.discountSnapshot?.code)} onChange={(event) => setCheckoutEmail(event.target.value)} required />{hold.paymentRequirement.discountSnapshot?.code ? <small>The applied code is tied to this reservation email.</small> : null}</label>
              <label className="full">Mobile phone<Input name="phone" type="tel" autoComplete="tel" placeholder="+1 775 555 0123" defaultValue={bookingProfile?.phone} required /></label>
              {hold.questions.map((question) => <label className="full" key={question.id}>{question.label}<Input name={question.id} required={question.required} /></label>)}
            </div>
            {hold.paymentRequirement.discountAmount ? (
              <div className="booking-discount-applied" role="status"><Check /><span><strong>{hold.paymentRequirement.discountSnapshot?.name ?? 'Discount applied'}</strong><small>{hold.paymentRequirement.discountSnapshot?.code ? `Code ${hold.paymentRequirement.discountSnapshot.code}` : 'Your eligible rate is included in the total above.'}</small></span></div>
            ) : (
              <details className="booking-discount-entry">
                <summary>Add a discount code</summary>
                <div>
                  <label>Discount code<Input value={discountCode} maxLength={80} onChange={(event) => setDiscountCode(event.target.value.toUpperCase().replaceAll(/\s+/g, ''))} placeholder="Enter code" /></label>
                  <Button type="button" variant="outline" disabled={!discountCode.trim() || !checkoutEmail.trim() || applyingDiscount} onClick={() => void handleApplyDiscount()}>{applyingDiscount ? 'Applying…' : 'Apply'}</Button>
                  <small>We’ll validate this code using the reservation email above and update your total before payment.</small>
                </div>
              </details>
            )}
            {error && <p className="booking-error" role="alert">{error}</p>}
            <label className="native-check"><input name="termsAccepted" type="checkbox" required /> I agree to the reservation and cancellation terms shown below.</label>
            <label className="native-check"><input name="smsTransactional" type="checkbox" /> Text me a reservation reminder and service updates.</label>
            <label className="native-check"><input name="emailMarketing" type="checkbox" /> Send me occasional restaurant news and offers. This is optional.</label>
            <div className="booking-policy"><strong>Cancellation policy</strong><p>{String(hold.policySnapshot.termsText ?? 'Contact the venue to modify or cancel.')}</p></div>
            <Button type="submit" size="lg" className="button button--burgundy button--full" disabled={submitting || applyingDiscount}>{submitting ? 'Preparing checkout…' : hold.paymentRequirement.bookingMethod !== 'free' ? 'Continue to secure payment' : 'Confirm reservation'}</Button>
            <p className="checkout-disclosure">By confirming, you agree to Trellis Trellis Tables’ terms and privacy policy. Transactional reservation messages are sent separately from optional marketing.</p>
          </form>
        )}

        {step === 'payment' && paymentSession && pendingConfirmation && (
          <StripePaymentStep session={paymentSession} savedPaymentMethods={savedPaymentMethods} onBack={() => setStep('details')} onComplete={() => confirmReservation(pendingConfirmation)} />
        )}

        {step === 'notify' && (
          <form className="booking-body booking-details notify-details" onSubmit={requestNotify}>
            <p className="step-label">Get notified</p>
            <h2>{notifyTime ? `Get notified for ${formatClock(notifyTime)}.` : 'Choose your time range.'}</h2>
            <p className="notify-intro">{notifyTime ? 'We’ll email you if this time becomes available.' : 'Choose the times that work for you.'}</p>
            <div className="booking-summary"><strong>{venue.name}</strong><span><CalendarDays size={15} /> {formatDate(date)}</span>{notifyTime ? <span><Clock3 size={15} /> {formatClock(notifyTime)}</span> : null}<span><Users size={15} /> {party} guests</span></div>
            <div className="form-grid notify-window">
              {notifyTime ? <><input name="timeFrom" type="hidden" value={notifyTime} /><input name="timeUntil" type="hidden" value={addMinutesToClock(notifyTime, 15)} /></> : <><label>From<Input name="timeFrom" type="time" step="900" defaultValue="17:00" required /></label><label>Until<Input name="timeUntil" type="time" step="900" defaultValue="21:00" required /></label></>}
              <label className="full">Name<Input name="name" autoComplete="name" required /></label>
              <label className="full">Email<Input name="email" type="email" autoComplete="email" required /></label>
              <label className="full">Phone <span>Optional</span><Input name="phone" type="tel" autoComplete="tel" /></label>
              <label className="full">Notes <span>Optional</span><Input name="notes" placeholder="Timing flexibility or seating needs" /></label>
            </div>
            {error && <p className="booking-error" role="alert">{error}</p>}
            <Button type="submit" size="lg" className="button button--burgundy button--full" disabled={submitting}>{submitting ? 'Saving request…' : 'Notify me'}</Button>
          </form>
        )}

        {step === 'notify-confirmed' && (
          <div className="confirmed-state notify-confirmed-state">
            <div className="confirmed-icon"><BellRing /></div>
            <p className="eyebrow">Notifications on</p>
            <h2>We’ll email you if a table opens.</h2>
            <p>Openings are offered to everyone who requested this time and are not held.</p>
            <Button size="lg" className="button button--burgundy button--full" onClick={onClose}>Done</Button>
          </div>
        )}

        {step === 'confirmed' && confirmation && (
          <div className="confirmed-state">
            <div className="confirmed-icon"><Check /></div>
            <p className="eyebrow">Confirmed</p>
            <h2>Reservation confirmed.</h2>
            <p>Your reservation at <strong>{venue.name}</strong> is confirmed for <strong>{formatTime(confirmation.startsAt)}</strong>{hold ? ` on ${formatDate(hold.businessDate)}` : ''}.</p>
            {hold && <div className="confirmation-summary"><span><CalendarDays /> {formatDate(hold.businessDate)} · {formatTime(confirmation.startsAt)}</span><span><Users /> {confirmation.partySize} guests</span></div>}
            <div className="confirmation-code"><small>Confirmation</small><strong>{confirmation.confirmationCode}</strong></div>
            <Button size="lg" className="button button--burgundy button--full" asChild><Link href={bookingProfile ? `/account/reservations/${confirmation.reservationPublicId}` : `/manage/${confirmation.managementToken}`}>Manage reservation</Link></Button>
            <button className="confirmation-done" onClick={onClose}>Done</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

const formatTime = (value: string) => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' }).format(new Date(value))
const formatDate = (value: string) => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`))
const formatMoney = (amount: number, currency: string) => new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100)

function formatClock(value: string) {
  const [hours, minutes] = value.split(':').map(Number)
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2026, 0, 1, hours, minutes)))
}

function addMinutesToClock(value: string, addedMinutes: number) {
  const [hours, minutes] = value.split(':').map(Number)
  const total = (hours * 60 + minutes + addedMinutes) % (24 * 60)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

function todayDate() {
  return toLocalDate(new Date())
}

function dateValue(value: string) {
  return new Date(`${value}T12:00:00`)
}

function toLocalDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
